type PrefillQuestionOption = {
  label?: string;
  step_question?: { step_id?: string };
};

export type PrefillQuestionLike = {
  id: number | string;
  label: string;
  type?: string;
  is_required?: boolean;
  step_id?: string;
  default_value?: string;
  options?: PrefillQuestionOption[] | null;
};

export type PrefillParseResult = {
  prefillPresent: boolean;
  prefillParseError: boolean;
  prefillData: Record<string, string>;
};

type TypedPrefillValues = {
  contactName?: string;
  customerName?: string;
  email?: string;
  phone?: string;
};

type PrefillCompletenessArgs = {
  questions: PrefillQuestionLike[];
  prefillData?: Record<string, string> | null;
  typedPrefill?: TypedPrefillValues;
  visibleStepIds?: string[];
  businessEmail?: boolean;
  organisationId?: number;
  deriveOrg9EmailFromPhone?: boolean;
};

export type PrefillCompletenessResult = {
  isComplete: boolean;
  answersByQuestionId: Record<string, string>;
  missingRequired: { id: string; label: string }[];
  invalidFields: { id: string; label: string; reason: string }[];
  visibleStepIds: string[];
  requiredFieldCount: number;
};

const FREE_MAIL_PROVIDERS = new Set([
  "gmail.com",
  "yahoo.com",
  "outlook.com",
  "protonmail.com",
  "rediffmail.com",
  "icloud.com",
  "aol.com",
]);

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
};

const toStringRecord = (value: unknown): Record<string, string> | null => {
  if (!isRecord(value)) return null;
  const result: Record<string, string> = {};
  Object.entries(value).forEach(([key, item]) => {
    if (typeof item === "string") {
      result[key] = item;
      return;
    }
    if (typeof item === "number" || typeof item === "boolean") {
      result[key] = String(item);
    }
  });
  return result;
};

export const parsePrefillValue = (rawPrefill: unknown): PrefillParseResult => {
  if (!rawPrefill) {
    return {
      prefillPresent: false,
      prefillParseError: false,
      prefillData: {},
    };
  }

  if (isRecord(rawPrefill)) {
    return {
      prefillPresent: true,
      prefillParseError: false,
      prefillData: toStringRecord(rawPrefill) || {},
    };
  }

  if (typeof rawPrefill !== "string") {
    return {
      prefillPresent: true,
      prefillParseError: true,
      prefillData: {},
    };
  }

  const parseAttempts = [
    () => JSON.parse(decodeURIComponent(rawPrefill)),
    () => JSON.parse(rawPrefill),
  ];

  for (const parse of parseAttempts) {
    try {
      const parsed = parse();
      return {
        prefillPresent: true,
        prefillParseError: false,
        prefillData: toStringRecord(parsed) || {},
      };
    } catch (_error) {
      // Try next strategy.
    }
  }

  return {
    prefillPresent: true,
    prefillParseError: true,
    prefillData: {},
  };
};

export const normalizePrefillPhone = (phone?: string): string => {
  if (!phone) return "";
  let value = String(phone);
  if (value.includes(" ") && value.indexOf(" ") === 0) {
    value = value.replace(" ", "+");
  }
  if (value.includes(" ")) {
    value = value.replaceAll(" ", "");
  }
  return value;
};

const getTypedFallbackValue = (
  question: PrefillQuestionLike,
  typedPrefill: TypedPrefillValues
): string => {
  const type = question?.type || "";
  if (type === "zt_contact_name") return typedPrefill.contactName || "";
  if (type === "zt_customer_name") return typedPrefill.customerName || "";
  if (type === "zt_contact_email" || type === "email") return typedPrefill.email || "";
  if (type === "zt_contact_phone_number" || type === "phone_number") {
    return typedPrefill.phone || "";
  }
  return "";
};

const isQuestionVisible = (question: PrefillQuestionLike, visibleStepIds: Set<string>): boolean => {
  if (!question?.step_id) return true;
  return visibleStepIds.has(String(question.step_id));
};

const isValidEmail = (value: string): boolean => {
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,20}$/i.test(value);
};

const isValidPhone = (value: string): boolean => {
  return /^[+-\s()0-9]{10,15}$/g.test(value);
};

const isBusinessEmailAccepted = (value: string): boolean => {
  const domain = (value.split("@")[1] || "").toLowerCase();
  if (!domain) return false;
  return !FREE_MAIL_PROVIDERS.has(domain);
};

const deriveVisibleStepsFromAnswers = (
  questions: PrefillQuestionLike[],
  answersByQuestionId: Record<string, string>,
  seedVisibleStepIds?: string[]
): string[] => {
  const visibleStepIds = new Set((seedVisibleStepIds || []).map(String));
  let changed = true;
  let safetyCounter = 0;

  while (changed && safetyCounter < Math.max(questions.length, 1)) {
    changed = false;
    safetyCounter += 1;

    questions.forEach((question) => {
      if ((question?.type || "") !== "radio_button") return;
      if (!isQuestionVisible(question, visibleStepIds)) return;

      const selectedValue = answersByQuestionId[String(question.id)] || "";
      if (!selectedValue || !question?.options?.length) return;

      const selectedOption = question.options.find(
        (option) => (option?.label || "") === selectedValue
      );
      const stepId = selectedOption?.step_question?.step_id;
      if (stepId && !visibleStepIds.has(String(stepId))) {
        visibleStepIds.add(String(stepId));
        changed = true;
      }
    });
  }

  return Array.from(visibleStepIds);
};

export const evaluatePrefillCompleteness = ({
  questions,
  prefillData,
  typedPrefill,
  visibleStepIds,
  businessEmail,
  organisationId,
  deriveOrg9EmailFromPhone,
}: PrefillCompletenessArgs): PrefillCompletenessResult => {
  const safeQuestions = questions || [];
  const safePrefill = prefillData || {};
  const typedValues = {
    contactName: typedPrefill?.contactName || "",
    customerName: typedPrefill?.customerName || "",
    email: typedPrefill?.email || "",
    phone: typedPrefill?.phone || "",
  };

  const answersByQuestionId: Record<string, string> = {};

  safeQuestions.forEach((question) => {
    const qid = String(question?.id ?? "");
    if (!qid) return;

    let value = safePrefill[question?.label || ""] || "";
    if (!value) {
      value = getTypedFallbackValue(question, typedValues);
    }

    if (!value && (question?.type || "") === "disclaimer") {
      value = question?.default_value || "N";
    }

    if (
      !value &&
      deriveOrg9EmailFromPhone &&
      organisationId === 9 &&
      ((question?.type || "") === "email" || (question?.type || "") === "zt_contact_email")
    ) {
      value = `no-reply${typedValues.phone || ""}@leadschool.in`;
    }

    answersByQuestionId[qid] = value;
  });

  const derivedVisibleStepIds = deriveVisibleStepsFromAnswers(
    safeQuestions,
    answersByQuestionId,
    visibleStepIds
  );
  const visibleStepIdSet = new Set(derivedVisibleStepIds.map(String));

  const missingRequired: { id: string; label: string }[] = [];
  const invalidFields: { id: string; label: string; reason: string }[] = [];
  let requiredFieldCount = 0;

  safeQuestions.forEach((question) => {
    const qid = String(question?.id ?? "");
    if (!qid) return;
    if (!isQuestionVisible(question, visibleStepIdSet)) return;

    if (
      organisationId === 9 &&
      deriveOrg9EmailFromPhone &&
      ((question?.type || "") === "email" || (question?.type || "") === "zt_contact_email")
    ) {
      return;
    }

    if (!question?.is_required) return;
    requiredFieldCount += 1;

    const rawValue = answersByQuestionId[qid];
    const value = String(rawValue || "").trim();
    if (!value) {
      missingRequired.push({ id: qid, label: question?.label || qid });
      return;
    }

    const type = question?.type || "";
    if ((type === "email" || type === "zt_contact_email") && !isValidEmail(value)) {
      invalidFields.push({ id: qid, label: question?.label || qid, reason: "invalid_email" });
      return;
    }

    if (type === "zt_contact_email" && businessEmail && !isBusinessEmailAccepted(value)) {
      invalidFields.push({
        id: qid,
        label: question?.label || qid,
        reason: "business_email_required",
      });
      return;
    }

    if ((type === "phone_number" || type === "zt_contact_phone_number") && !isValidPhone(value)) {
      invalidFields.push({ id: qid, label: question?.label || qid, reason: "invalid_phone" });
      return;
    }

    if (type === "radio_button" && question?.options?.length) {
      const isMatch = question.options.some((option) => (option?.label || "") === rawValue);
      if (!isMatch) {
        invalidFields.push({ id: qid, label: question?.label || qid, reason: "invalid_option" });
      }
    }
  });

  return {
    isComplete: !missingRequired.length && !invalidFields.length && requiredFieldCount >= 0,
    answersByQuestionId,
    missingRequired,
    invalidFields,
    visibleStepIds: derivedVisibleStepIds,
    requiredFieldCount,
  };
};

